import { Dropdown, type DropdownProps } from 'semantic-ui-react';
import styled from 'styled-components';
import { CaretDown, MagnifyingGlass, XCircle } from '@phosphor-icons/react';
import React from 'react';

// ---------- Interfaces ---------- //

interface IStyledDropdownProps extends DropdownProps {
  innerTextPos?: number;
}

interface IIconProps {
  topSpace?: string | number;
  size?: number;
}

interface ICenterDropdownStyleProps extends DropdownProps {
  loading?: boolean;
  iconSize?: number;
  iconTopSpace?: string | number;
  searchIcon?: boolean;
}

interface ICountryDropdownStyleProps extends DropdownProps {
  loading?: boolean;
}

interface ITitleDropdownProps extends DropdownProps {
  loading?: boolean;
}

// ---------- Styled Components ---------- //

const StyledDropdown = styled(Dropdown)<IStyledDropdownProps>`
  box-sizing: border-box !important;
  border-radius: 5px !important;
  color: ${({ theme }) => theme.colors.black} !important;
  font-size: 12px !important;
  min-height: 38px !important;
  display: flex !important;
  align-items: center !important;

  input {
    margin-top: 5px;
  }

  &:not(&.mute-dropdown) {
    border: 0.5px solid ${({ theme }) => theme.colors.grey} !important;
  }

  &:focus-within {
    border-width: 1px !important;
  }

  &.selection.active.dropdown .menu {
    border-color: ${({ theme }) => theme.colors.grey} !important;
  }

  .divider.text {
    top: ${({ innerTextPos }) => innerTextPos || 0}px;
    display: flex;
    align-items: center;
    column-gap: 5px;
  }

  .item {
    display: flex !important;
    align-items: center !important;
    column-gap: 5px !important;
    min-height: 40px;
  }

  img.ui.image {
    margin-bottom: unset !important;
  }

  .divider.default {
    top: 0px;
  }

  .dropdown.icon {
    top: 11px !important;
  }

  &.ui.selection.dropdown.active,
  .ui.selection.dropdown.visible {
    z-index: 20;
  }
`;

const DropdownIcon = styled(CaretDown)<IIconProps>`
  cursor: pointer;
  position: absolute;
  width: auto;
  height: auto;
  line-height: 1.21428571em;
  top: ${({ topSpace }) => topSpace || '0.78571429em'};
  right: 0.7em;
  z-index: 3;
  margin: -0.78571429em;
  padding: 0.7em;
  opacity: 0.8;
  transition: opacity 0.1s ease;
`;

const ClearIcon = styled(XCircle)<IIconProps>`
  cursor: pointer;
  position: absolute;
  top: ${({ topSpace }) => topSpace || '0.78571429em'};
  right: 0.7em;
  z-index: 3;
`;

const SearchIcon = styled(MagnifyingGlass)<IIconProps>`
  cursor: pointer;
  position: absolute;
  top: ${({ topSpace }) => topSpace || '0.78571429em'};
  right: 0.7em;
  z-index: 3;
`;

// ---------- Components ---------- //

export function CenterDropdownStyle({
  loading,
  iconSize = 18,
  iconTopSpace,
  searchIcon,
  ...props
}: ICenterDropdownStyleProps) {
  return (
    <StyledDropdown
      icon={
        loading ? (
          <i className="dropdown icon" aria-hidden="true" />
        ) : props.clearable === true && props.value ? (
          <ClearIcon size={iconSize} topSpace={iconTopSpace} />
        ) : searchIcon ? (
          <SearchIcon size={iconSize} topSpace={iconTopSpace} />
        ) : (
          <DropdownIcon size={iconSize} topSpace={iconTopSpace} />
        )
      }
      loading={loading}
      {...props}
    />
  );
}

// ---------- Country Dropdown ---------- //

const DropdownCountryStyle = styled(StyledDropdown)`
  min-width: 11rem !important;

  .divider .image {
    margin-top: 0px !important;
  }

  &:not(&.mute-dropdown) {
    border: 0.5px solid ${({ theme }) => theme.colors.grey} !important;
  }

  a.ui.label {
    background: ${({ theme }) => theme.colors.box};
    box-shadow: none !important;
    border-radius: 15px;
    img {
      margin-right: 5px;
      display: inherit;
      width: 20px !important;
      height: unset !important;
    }
    display: flex !important;
    align-items: center;
  }

  div.divider.text {
    display: flex;
    align-items: center;
    img.ui.image {
      margin-top: unset;
      margin-bottom: unset;
    }
  }

  &.ui.multiple.search {
    display: flex !important;
    flex-wrap: wrap !important;
  }

  div.item {
    min-height: 30px;
  }

  &&&& {
    div.item {
      padding-left: 12px !important;
      padding-right: 12px !important;
    }
  }
`;

export function CountryDropdownStyle({ loading, ...props }: ICountryDropdownStyleProps) {
  return (
    <DropdownCountryStyle
      icon={
        loading ? (
          <i className="dropdown icon" aria-hidden="true" />
        ) : props.clearable === true && props.value ? (
          <ClearIcon size={18} />
        ) : (
          <DropdownIcon size={18} />
        )
      }
      loading={loading}
      {...props}
    />
  );
}

// ---------- Title Dropdown ---------- //

const StyledTitleDropdown = styled(Dropdown)`
  color: ${({ theme }) => theme.colors.grey} !important;
  font-size: 12px !important;
  font-weight: 400 !important;
  background: ${({ theme }) => theme.colors.primaryLight} !important;
  white-space: nowrap;
  padding: 5px 10px;
  padding-right: 30px;
  border-radius: 5px;
  min-height: 30px !important;
  max-height: 30px !important;

  .divider.text {
    color: ${({ theme }) => theme.colors.grey} !important;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 130px;
  }
`;

const TitleDropdownIcon = styled(CaretDown)`
  cursor: pointer;
  position: absolute;
  width: auto;
  height: auto;
  line-height: 1.21428571em;
  top: 7px;
  right: 5px;
  z-index: 3;
  opacity: 0.8;
  transition: opacity 0.1s ease;
`;

export function TitleDropdown({ loading, ...props }: ITitleDropdownProps) {
  return (
    <StyledTitleDropdown
      icon={
        loading ? (
          <i className="dropdown icon" aria-hidden="true" />
        ) : (
          <TitleDropdownIcon size={16} />
        )
      }
      loading={loading}
      {...props}
    />
  );
}
